import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerBody,
  DrawerFooter,
  DrawerCloseButton,
  Button,
  Text,
  Flex,
  useToast,
  Box,
} from "@chakra-ui/react";
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
  Elements,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import axios from "axios";
import React, { useEffect, useState } from "react";

// Local dev can point payment calls at a local engine via REACT_APP_PAY_API_URL;
// production builds don't set it and fall back to the live API.
const PAY_API = process.env.REACT_APP_PAY_API_URL || "https://api.linkbase.ca";

const DepositCardDrawerContent = ({
  isOpen,
  onClose,
  stripeAccountId,
  fullSecurityItem,
  totalPrice,
  booking,
  onPaymentSuccess,
}) => {
  const toast = useToast();
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const [expiryComplete, setExpiryComplete] = useState(false);
  const [cvcComplete, setCvcComplete] = useState(false);

  const handlePayment = async () => {
    if (!stripe || !elements) return;

    setLoading(true);

    const cardElement = elements.getElement(CardNumberElement);

    if (!cardElement) {
      toast({
        title: "Input Information",
        description: "Please input card information.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      setLoading(false);
      return;
    }

    try {
      // Step 1: Create setup intent
      const { Auth } = await import("aws-amplify");
      const session = await Auth.currentSession();
      const idToken = session.getIdToken().getJwtToken();

      const setupIntentResponse = await axios.post(
        `${PAY_API}/payments/setup-intent/booking`,
        {
          bookingId: booking.id,
        },
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json',
            'x-api-key': process.env.REACT_APP_LINKBASE_API_KEY,
          },
        }
      );

      console.log('Setup Intent Response:', setupIntentResponse.data);

      if (!setupIntentResponse.data?.success) {
        console.error('Setup intent failed:', setupIntentResponse.data);
        toast({
          title: "Setup Intent Error",
          description: setupIntentResponse.data?.error?.message || "Could not create setup intent.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
        setLoading(false);
        return;
      }

      const setupIntentId = setupIntentResponse.data.setupIntentId;
      const clientSecret = setupIntentResponse.data.clientSecret;

      if (!setupIntentId || !clientSecret) {
        toast({
          title: "Setup Intent Error",
          description: "Invalid setup intent response.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
        setLoading(false);
        return;
      }

      // Step 2: Confirm the setup intent with the card details
      // Note: Stripe instance is already initialized with stripeAccount
      const { error: setupError, setupIntent } = await stripe.confirmCardSetup(
        clientSecret,
        {
          payment_method: {
            card: cardElement,
          },
        }
      );

      if (setupError) {
        toast({
          title: "Payment Setup Failed",
          description: setupError.message,
          status: "error",
          duration: 4000,
          isClosable: true,
        });
        setLoading(false);
        return;
      }

      // Step 3: Calculate scheduledAt (1 day before checkout at 6 AM) and get timezone
      const checkoutDate = new Date(booking.checkOutDate);
      const scheduledDate = new Date(checkoutDate);
      scheduledDate.setDate(scheduledDate.getDate() - 1); // 1 day before checkout
      
      // Format as YYYY-MM-DD and set time to 06:00:00
      const dateOnly = scheduledDate.toISOString().split('T')[0];
      const scheduledAt = `${dateOnly}T06:00:00`; // 6 AM on the day before checkout
      
      // Get user's timezone
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      console.log('Confirm and charge params:', {
        bookingId: booking.id,
        scheduledAt,
        timezone,
        checkOutDate: booking.checkOutDate
      });

      // Step 4: Confirm and charge (backend extracts payment method from SetupIntent automatically)
      const confirmResponse = await axios.post(
        `${PAY_API}/payments/setup-intent/${setupIntentId}/confirm-and-charge`,
        {
          bookingId: booking.id,
          scheduledAt: scheduledAt,
          timezone: timezone,
        },
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json',
            'x-api-key': process.env.REACT_APP_LINKBASE_API_KEY,
          },
        }
      );

      if (!confirmResponse.data?.success) {
        toast({
          title: "Charge Failed",
          description: confirmResponse.data?.error?.message || "Could not process the deposit.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
        setLoading(false);
        return;
      }

      // Success! Backend already marked it as complete
      const paymentIntentId = confirmResponse.data.data?.paymentIntentId;

      // Show success toast
      toast({
        title: "You're All Set",
        description:
          "Deposit secured. Nothing else to do — everything happens automatically.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });

      // Call success callback to refresh booking status
      if (onPaymentSuccess) {
        onPaymentSuccess(paymentIntentId);
      }

      // Close the drawer
      onClose();
    } catch (err) {
      console.error("Deposit intent error:", err);
      toast({
        title: "Error",
        description: err.response?.data?.error?.message || "Something went wrong during deposit.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer isOpen={isOpen} placement="bottom" onClose={onClose}>
      <DrawerOverlay />
      <DrawerContent
        minH="35vh"
        borderTopRadius="2xl"
        maxW={{ base: "100%", md: "520px" }}
        mx="auto"
        mb={{ md: 8 }}
        borderRadius={{ md: "24px" }}
      >
        <Flex mx={3} my={4} align="center" justify="space-between">
          <Text fontSize="18px" fontWeight={800}>
            Card for Damage Deposit
          </Text>
          <DrawerCloseButton pos="relative" top={0} right={0} />
        </Flex>
        <DrawerBody p={3}>
          <Text fontSize="12px" fontWeight={600} color="gray.300" pb={4}>
            No deposit is charged — a temporary hold is placed before
            check-out and released after your stay.
          </Text>
          <Text fontWeight={800} fontSize="12px" pb="5px">
            Card Number
          </Text>
          <Box px={3} py={3} borderRadius="md" bg="#3e4757" mb={4} w="full">
            <CardNumberElement
              options={{
                style: {
                  base: {
                    fontSize: "16px",
                    color: "#fff",
                    "::placeholder": { color: "#ccc" },
                    iconColor: "#ccc",
                    fontFamily: "inherit",
                  },
                },
              }}
              onChange={e => setCardComplete(e.complete)}
            />
          </Box>
          <Flex gap={4}>
            <Flex flex={1} flexDirection="column">
              <Text fontWeight={800} fontSize="12px" pb="5px">
                Expiry
              </Text>
              <Box px={3} py={3} borderRadius="md" bg="#3e4757" w="full">
                <CardExpiryElement
                  options={{
                    style: {
                      base: {
                        fontSize: "16px",
                        color: "#fff",
                        "::placeholder": { color: "#ccc" },
                        fontFamily: "inherit",
                      },
                    },
                  }}
                  onChange={e => setExpiryComplete(e.complete)}
                />
              </Box>
            </Flex>
            <Flex flex={1} flexDirection="column">
              <Text fontWeight={800} fontSize="12px" pb="5px">
                CVC
              </Text>
              <Box px={3} py={3} borderRadius="md" bg="#3e4757" w="full">
                <CardCvcElement
                  options={{
                    style: {
                      base: {
                        fontSize: "16px",
                        color: "#fff",
                        "::placeholder": { color: "#ccc" },
                        fontFamily: "inherit",
                      },
                    },
                  }}
                  onChange={e => setCvcComplete(e.complete)}
                />
              </Box>
            </Flex>
          </Flex>
        </DrawerBody>
        <DrawerFooter p={3}>
          <Button
            onClick={handlePayment}
            isLoading={loading}
            bg="#817efc"
            color="white"
            h="42px"
            w="full"
            variant="solid"
            borderRadius="10px"
            fontSize="14px"
            disabled={!cardComplete || !expiryComplete || !cvcComplete}
            _disabled={{
              opacity: 0.5,
              cursor: "not-allowed",
              bg: "#817efc",
              color: "white",
              pointerEvents: "none"
            }}
          >
            Confirm Card
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export const DepositCardDrawer = ({
  isOpen,
  onClose,
  stripeAccountId,
  fullSecurityItem,
  totalPrice,
  booking,
  onPaymentSuccess,
}) => {
  const [stripePromise, setStripePromise] = useState(null);

  useEffect(() => {
    if (isOpen && stripeAccountId) {
      const loadConnectedStripe = async () => {
        const stripe = await loadStripe(process.env.REACT_APP_STRIPE_API_KEY, {
          stripeAccount: stripeAccountId
        });
        setStripePromise(stripe);
      };
      loadConnectedStripe();
    }
  }, [isOpen, stripeAccountId]);

  if (!stripePromise) {
    return null;
  }

  return (
    <Elements stripe={stripePromise}>
      <DepositCardDrawerContent
        isOpen={isOpen}
        onClose={onClose}
        stripeAccountId={stripeAccountId}
        fullSecurityItem={fullSecurityItem}
        totalPrice={totalPrice}
        booking={booking}
        onPaymentSuccess={onPaymentSuccess}
      />
    </Elements>
  );
};

